/**
 * CSV Export Utilities for Ranking Page
 * Exports raw aggregates and ranked IQUP scores
 */

import { FEATURE_META, CLUSTERING_FEATURES } from './clustering.js'

/**
 * Escape CSV field (handles commas, quotes, newlines)
 */
function escapeCSV(value) {
  if (value === null || value === undefined) return ''
  const str = String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/**
 * Convert array of objects to CSV string
 */
function toCSV(headers, rows) {
  const headerLine = headers.map(escapeCSV).join(',')
  const dataLines = rows.map(row =>
    headers.map(h => escapeCSV(row[h])).join(',')
  )
  return [headerLine, ...dataLines].join('\n')
}

/**
 * Trigger file download
 */
function downloadCSV(content, filename) {
  const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

/**
 * Export raw aggregates data (clustering features)
 * @param {Array} polos - Array of polo objects with aggregates
 * @param {Set} nivelFilter - Set of nivel values to include
 */
export function exportRawData(polos, nivelFilter) {
  const filtered = polos.filter(p => nivelFilter.has(p.nivel))

  // Build headers: basic info + all clustering features
  const headers = ['id', 'nome', 'nivel', ...CLUSTERING_FEATURES]

  // Build rows
  const rows = filtered.map(polo => {
    const row = {
      id: polo.id,
      nome: polo.nome,
      nivel: polo.nivel
    }

    // Add all clustering features
    CLUSTERING_FEATURES.forEach(key => {
      const value = polo.aggregates[key]
      const meta = FEATURE_META[key]

      if (meta.type === 'boolean') {
        row[key] = value ? 1 : 0
      } else {
        row[key] = value ?? ''
      }
    })

    return row
  })

  const csv = toCSV(headers, rows)
  const date = new Date().toISOString().split('T')[0]
  downloadCSV(csv, `polos_dados_brutos_${date}.csv`)
}

export function buildRankedRows(polos, nivelFilter) {
  const filtered = polos.filter(p => nivelFilter.has(p.nivel))

  return filtered.map((polo, index) => {
    const { Conectividade, Diversidade, Conforto } = polo.indicadores.Componentes

    return {
      posicao: index + 1,
      id: polo.id,
      nome: polo.nome,
      nivel: polo.nivel,
      iqup: polo.indicadores.Valor,
      conectividade: Conectividade.Valor,
      conectividade_ponto_nodal: Conectividade.Componentes['Ponto Nodal'],
      conectividade_acessos: Conectividade.Componentes.Acessos.Valor,
      diversidade: Diversidade.Valor,
      diversidade_usos: Diversidade.Componentes.Usos.Valor,
      diversidade_arquitetura: Diversidade.Componentes.Arquitetura.Valor,
      conforto: Conforto.Valor,
      conforto_sensorial: Conforto.Componentes.Sensorial.Valor,
      conforto_passeio: Conforto.Componentes.Passeio.Valor
    }
  })
}

/**
 * Export ranked IQUP scores
 * @param {Array} polos - Array of polo objects with calculated indicadores
 * @param {Set} nivelFilter - Set of nivel values to include
 */
export function exportRankedData(polos, nivelFilter) {
  // Build headers
  const headers = [
    'posicao',
    'id',
    'nome',
    'nivel',
    'iqup',
    'conectividade',
    'conectividade_ponto_nodal',
    'conectividade_acessos',
    'diversidade',
    'diversidade_usos',
    'diversidade_arquitetura',
    'conforto',
    'conforto_sensorial',
    'conforto_passeio'
  ]

  const rows = buildRankedRows(polos, nivelFilter)

  const csv = toCSV(headers, rows)
  const date = new Date().toISOString().split('T')[0]
  downloadCSV(csv, `polos_ranking_iqup_${date}.csv`)
}
